import { withTransaction } from '@elastic/apm-rum-react'
import IconPerson from '@material-ui/icons/Person'
import IconKey from '@material-ui/icons/VpnKey'
import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useStore } from 'react-redux'
import { APP_MODULES, PAGE_URL } from '../../constants'
import {
  ENABLE_BATTERY_MANAGEMENT_FRONTEND,
  ENABLE_DASHBOARD_FRONTEND,
  ENABLE_HOME_PROJECT_SELECTOR_FRONTEND,
  ENABLE_SITE_VIEW,
  TOKEN
} from '../../constants/env'
import { APICompanySettingsResponse, APIProjectResponse } from '../../models'
import { navigate, redirectToBackendApp } from '../../router/redirections'
import { RouteAppComponentProps } from '../../router/routes'
import { useSites } from '../../services/LocationService'
import { useProjectsInBatch } from '../../services/ProjectService'
import { useSettings } from '../../services/SettingsService'
import { useUserMe } from '../../services/UserService'
import { Store } from '../../store'
import { ActionType as ErrorActions } from '../../store/error/actions'
import { Store as StoreError } from '../../store/error/reducer'
import { ActionType, ActionType as HomeActions } from '../../store/home/action'
import { AppStoreProjectFilter } from '../../store/home/reducer'
import { setPageTitle } from '../../utils/hook'
import { debounce } from '../../utils/timers'
import AppNotAuthorizedPage from '../AppNotAuthorized'
import styles from './view/AppHome.module.css'
import AppHomeView from './view/AppHomeView'

function App(props: RouteAppComponentProps): JSX.Element {
  const { t, i18n } = useTranslation()
  const store = useStore<Store>()

  const [projects, ..._AndProject$] = useProjectsInBatch<
    { page: number } & Record<string, string | number | undefined>
  >({
    page: 1,
    fields: 'id,name,url'
  })
  const [sites, setSiteQuery] = useSites({
    page: 1,
    page_size: 50,
    fields: 'id,url,url_view,name,code,latitude,longitude,projects'
  })
  const [loginUser] = useUserMe()
  const [settings] = useSettings()
  const projectSelected = useSelector<Store, Partial<APIProjectResponse>>(
    (store) => {
      return store.home.project || {}
    }
  )
  const projectCustomFilter = useSelector<Store, AppStoreProjectFilter>(
    (store) => store.home.projectsFilter
  )
  const isLoading = useSelector<Store, boolean>((store) => store.home.isLoading)
  const userLogged = useSelector<Store, string | undefined>((store) => {
    return store.home.user.name
  })
  const isNotAuthorized = useSelector<Store, boolean>(
    (store) =>
      store.error.statusCode === 401 ||
      store.error.statusCode === 403 ||
      store.error.statusCode === 422
  )
  const isNotFound = useSelector<Store, boolean>(
    (store) => store.error.statusCode === 404
  )
  const isInternalError = useSelector<Store, boolean>(
    (store) =>
      store.error.statusCode !== 401 &&
      store.error.statusCode !== 404 &&
      store.error.statusCode !== 403 &&
      store.error.statusCode !== 422 &&
      store.error.statusCode !== 200 &&
      store.error.statusCode !== 400 &&
      store.error.statusCode !== 0
  )
  const errors = useSelector<Store, StoreError>((store) => store.error)

  const debounceSetSite = useCallback(
    debounce(500)((name) => {
      setSiteQuery({
        page: 1,
        page_size: 50,
        fields: 'id,url,url_view,name,code,latitude,longitude,projects',
        q: name
      })
    }),
    ['', setSiteQuery]
  )

  useEffect(() => {
    store.dispatch({
      type: ActionType.SET_PROJECTS,
      payload: { projects: projects || [] }
    })
  }, [projects])

  useEffect(() => {
    store.dispatch({
      type: ActionType.SET_USER,
      payload: { user: loginUser?.data || {} }
    })
  }, [loginUser])

  useEffect(() => {
    setPageTitle(window)(props.pageName)()
  }, [])

  useEffect(() => {
    const language = settings?.data
      .filter((data) => data.name === 'LANGUAGE')
      .reduce<APICompanySettingsResponse | undefined>(
        (acc, current) => acc || current,
        undefined
      )
    language?.value && i18n.changeLanguage(String(language.value))
  }, [settings])

  useEffect(() => {
    store.dispatch({
      type: ActionType.SET_SITES,
      payload: {
        sites: Array.isArray(sites) ? sites : []
      }
    })
  }, [sites])

  useEffect(() => {
    store.dispatch({
      type: ActionType.SET_ARE_PROJECTS_LOADING,
      payload: {
        areProjectsLoading: true
      }
    })
    if (!_AndProject$[1]) return
    const subscription = _AndProject$[1].subscribe({
      complete: () => {
        store.dispatch({
          type: ActionType.SET_ARE_PROJECTS_LOADING,
          payload: {
            areProjectsLoading: false
          }
        })
      }
    })
    return () => {
      subscription.unsubscribe()
    }
  }, [_AndProject$[1]])

  useEffect(() => {
    if (TOKEN?.length < 40 || TOKEN.match(RegExp('[-_/*+]', 'g'))) {
      redirectToBackendApp(window.location)(PAGE_URL.LOGIN)
    }
  }, [])

  function onCloseNotificationListener(
    event?: React.SyntheticEvent,
    reason?: string
  ): void {
    if (reason === 'clickaway') return
    store.dispatch({
      type: ErrorActions.SET_ERROR,
      payload: { isShown: false }
    })
  }

  return isNotAuthorized ? (
    <AppNotAuthorizedPage {...props} pageName='Not Authorized' />
  ) : (
    <AppHomeView
      {...props}
      isLoading={isLoading}
      isNotFound={isNotFound}
      isInternalError={isInternalError}
      alertMenu={{
        menuOptions: [],
        onMenuSelected: (event) => {
          console.log(event)
        }
      }}
      notificationMenu={{
        menuOptions: [],
        onMenuSelected: (event) => {
          console.log(event)
        }
      }}
      userMenu={{
        menuOptions: [
          {
            label: t('My Profile'),
            icon: (
              <IconPerson
                className={styles.menuOptionsIcon}
                color='primary'
                fontSize='small'
              />
            )
          },
          {
            label: t('Logout'),
            icon: (
              <IconKey
                className={styles.menuOptionsIcon}
                color='primary'
                fontSize='small'
              />
            )
          }
        ],
        menuName: userLogged,
        onMenuSelected: (event) => {
          switch (event?.key) {
            case 0:
              return redirectToBackendApp(window.location)('accounts/profile')
            case 1:
              return redirectToBackendApp(window.location)('accounts/logout')
            default:
              return
          }
        }
      }}
      projectMenu={{
        menuOptions: [
          { label: t('All-Projects') },
          ...((projects || [])
            .filter(projectCustomFilter)
            .map((project) => ({ label: project.name })) || [])
        ],
        onMenuSelected: (event) => {
          store.dispatch({
            type: HomeActions.SET_PROJECT,
            payload: {
              project: event?.key
                ? (projects || []).filter(projectCustomFilter)[event.key - 1]
                : undefined
            }
          })
        }
      }}
      enableBatteryManagementFrontEnd={ENABLE_BATTERY_MANAGEMENT_FRONTEND}
      enableDashboardFrontEnd={ENABLE_DASHBOARD_FRONTEND}
      showProjects={ENABLE_HOME_PROJECT_SELECTOR_FRONTEND}
      projectSelected={projectSelected.name || ''}
      searchOptions={
        sites?.data.map((site) => `${site.code}-${site.name}`) || []
      }
      onSearchOptionSelected={(event) => {
        if (ENABLE_SITE_VIEW) {
          return navigate(props.history)(
            PAGE_URL.SITE_VIEW.replace(/:id/, `${sites?.data[event].id}`)
          )
        }
        redirectToBackendApp(window.location)(sites?.data[event].url_view || '')
      }}
      onAutocomplete={(event) => debounceSetSite(event.target.value)()}
      onLogoClick={() => {
        setPageTitle(window)('Sitehub')()
        navigate(props.history)(PAGE_URL.HOME)
      }}
      snackbar={{
        open: errors.isShown,
        body: <p>{errors.message}</p>,
        duration: errors.duration,
        onClose: onCloseNotificationListener,
        severity: errors.severity || 'warning'
      }}
      moduleAvailable={
        projectSelected.modules?.map((projectModule) => projectModule.name) ||
        Object.values(APP_MODULES)
      }
    />
  )
}

export default withTransaction('App', 'component')(App)
